"use client";

import React from "react";
import { Phone, Clock, TrendingUp, Loader2, Zap, Layers, Hand } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/app/_module/components/ui/card";
import { useCommunicationStats, useDialerBreakdown } from "@/app/_module/hooks/useCommunicationService";
import { useBranding } from "@/app/_module/store/contexts/brandingContext";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

/** Follows the dashboard's date filter; omitting the range keeps the all-time behaviour. */
export const DialTimeStats = function ({
  startDate,
  endDate,
}: {
  startDate?: string;
  endDate?: string;
} = {}) {
  const range = { startDate, endDate };
  const { data: stats, isLoading, error } = useCommunicationStats(range);
  const { data: dialerBreakdown } = useDialerBreakdown(range);
  const { branding, isWhiteLabeled } = useBranding();
  const primaryColor = branding.primaryColor || "#2563eb";

  if (isLoading) {
    return (
      <Card className="w-full shadow-sm border-gray-200">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-lg font-semibold text-gray-900">
            Dial Time Stats
          </CardTitle>
          <Phone className="h-5 w-5 text-primary" />
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <div className="flex items-center gap-2 text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading dial time stats...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !stats) {
    return (
      <Card className="w-full shadow-sm border-gray-200">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-lg font-semibold text-gray-900">
            Dial Time Stats
          </CardTitle>
          <Phone className="h-5 w-5 text-primary" />
        </CardHeader>
        <CardContent className="text-center py-8 text-gray-500">
          <p className="text-sm">Failed to load dial time stats</p>
          {error && (
            <p className="text-xs mt-2 text-red-600">Error: {String(error)}</p>
          )}
          <p className="text-xs mt-2 text-gray-400">Data will auto-refresh</p>
        </CardContent>
      </Card>
    );
  }

  // Calculate metrics
  const totalCalls = stats.totalCalls || 0;
  const answeredCalls = stats.answeredCalls || 0;
  const totalTime = stats.totalTime || 0;
  const avgCallDuration = stats.avgCallDuration || 0;
  const callAnswerRate = stats.callAnswerRate || 0;

  // Format total time as hours and minutes
  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  // Format avg duration as minutes and seconds
  const formatAvgDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (mins > 0) {
      return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
  };

  // Prepare chart data
  const answeredColor = isWhiteLabeled ? primaryColor : "#16a34a";
  const chartData = [
    {
      name: "Answered",
      calls: answeredCalls,
      fill: answeredColor,
    },
    {
      name: "Missed/Failed",
      calls: totalCalls - answeredCalls,
      fill: "#ef4444",
    },
  ];

  return (
    <Card className="w-full flex flex-col shadow-sm border-gray-200">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-lg font-semibold text-gray-900">
          Dial Time Stats
        </CardTitle>
        <Phone className="h-5 w-5 text-primary" />
      </CardHeader>
      {/* Sections stay TOP-ALIGNED with even spacing; the chart below absorbs any
          leftover height (see its flex-1).
          Previously this was `justify-between`, added so the card never left a white gap
          at the bottom when the neighbouring card was taller. It worked, but it spread
          the four sections apart instead — trading one gap at the end for three in the
          middle, which reads as broken rather than merely short. Giving the extra height
          to the chart puts it somewhere useful: a taller chart is an improvement, empty
          space between sections never is. */}
      <CardContent className="flex-1 flex flex-col gap-5">
        {/* Summary Stats */}
        <div className="flex flex-col gap-2">
          <div className="p-2 bg-gradient-to-br from-primary/10 to-primary/20 rounded-lg border border-primary/20 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-primary" />
              <span className="text-xs text-primary font-medium">Total Calls</span>
            </div>
            <span className="text-base font-bold text-primary">{totalCalls}</span>
          </div>
          <div className={`p-2 bg-gradient-to-br ${isWhiteLabeled ? 'from-secondary/10 to-secondary/20 border-secondary/20' : 'from-green-50 to-green-100 border-green-200'} rounded-lg border flex items-center justify-between`}>
            <div className="flex items-center gap-2">
              <TrendingUp className={`h-4 w-4 ${isWhiteLabeled ? 'text-secondary' : 'text-green-600'}`} />
              <span className={`text-xs font-medium ${isWhiteLabeled ? 'text-secondary' : 'text-green-600'}`}>Successful</span>
            </div>
            <span className={`text-base font-bold ${isWhiteLabeled ? 'text-secondary' : 'text-green-600'}`}>{answeredCalls}</span>
          </div>
          <div className={`p-2 bg-gradient-to-br ${isWhiteLabeled ? 'from-primary/10 to-primary/20 border-primary/20' : 'from-blue-50 to-blue-100 border-blue-200'} rounded-lg border flex items-center justify-between`}>
            <div className="flex items-center gap-2">
              <Clock className={`h-4 w-4 ${isWhiteLabeled ? 'text-primary' : 'text-blue-600'}`} />
              <span className={`text-xs font-medium ${isWhiteLabeled ? 'text-primary' : 'text-blue-600'}`}>Total Time</span>
            </div>
            <span className={`text-base font-bold ${isWhiteLabeled ? 'text-primary' : 'text-blue-600'}`}>{formatTime(totalTime)}</span>
          </div>
          <div className={`p-2 bg-gradient-to-br ${isWhiteLabeled ? 'from-secondary/10 to-secondary/20 border-secondary/20' : 'from-orange-50 to-orange-100 border-orange-200'} rounded-lg border flex items-center justify-between`}>
            <div className="flex items-center gap-2">
              <Clock className={`h-4 w-4 ${isWhiteLabeled ? 'text-secondary' : 'text-orange-600'}`} />
              <span className={`text-xs font-medium ${isWhiteLabeled ? 'text-secondary' : 'text-orange-600'}`}>Avg Duration</span>
            </div>
            <span className={`text-base font-bold ${isWhiteLabeled ? 'text-secondary' : 'text-orange-600'}`}>{formatAvgDuration(avgCallDuration)}</span>
          </div>
        </div>

        {/* The elastic section. flex-1 + min-h-0 lets it soak up whatever height the
            card has spare, so surplus space becomes a taller chart rather than gaps
            between sections. min-h-0 is required — a flex child defaults to
            min-height:auto and would refuse to shrink below the chart's intrinsic size. */}
        <div className="flex flex-col flex-1 min-h-0">
          <h4 className="font-semibold text-gray-900 text-sm mb-3">
            Call Success Rate: {callAnswerRate.toFixed(1)}%
          </h4>
          {/* height="100%" so the chart tracks the flexed container instead of being
              pinned at 250px; minHeight keeps it legible when the card is short. */}
          <ResponsiveContainer width="100%" height="100%" minHeight={220}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="name"
                tick={{ fill: '#6b7280', fontSize: 12 }}
              />
              <YAxis
                tick={{ fill: '#6b7280', fontSize: 12 }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '12px'
                }}
              />
              <Bar
                dataKey="calls"
                fill={primaryColor}
                radius={[8, 8, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Additional Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-gray-200">
          <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
            <span className="text-xs font-medium text-gray-600">Answer Rate</span>
            <span className="text-sm font-bold text-gray-900">{callAnswerRate.toFixed(1)}%</span>
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
            <span className="text-xs font-medium text-gray-600">Missed Calls</span>
            <span className="text-sm font-bold text-gray-900">{totalCalls - answeredCalls}</span>
          </div>
        </div>

        {/* How You Dial — outbound calls by dialer (manual / power / multi-line) */}
        <DialerBreakdown data={dialerBreakdown} />
      </CardContent>
    </Card>
  );
};

// ── Dialer usage visual ───────────────────────────────────────────────────────
// A segmented usage bar + ranked rows so an agent sees at a glance which dialer
// they run most. Colors match the call-list dialer badges (amber=power,
// indigo=multi-line, slate=manual).
const DIALER_META = [
  { key: "powerDialer" as const, label: "Power Dialer", icon: Zap, color: "#f59e0b", bar: "bg-amber-500", chip: "bg-amber-100 text-amber-700" },
  { key: "multiLine" as const, label: "Multi-Line", icon: Layers, color: "#6366f1", bar: "bg-indigo-500", chip: "bg-indigo-100 text-indigo-700" },
  { key: "manual" as const, label: "Manual", icon: Hand, color: "#64748b", bar: "bg-slate-500", chip: "bg-slate-100 text-slate-700" },
];

function DialerBreakdown({
  data,
}: {
  data?: { manual: number; powerDialer: number; multiLine: number; total: number };
}) {
  const total = data?.total || 0;

  const rows = DIALER_META.map((m) => ({
    ...m,
    count: data ? data[m.key] : 0,
    pct: total > 0 ? Math.round(((data ? data[m.key] : 0) / total) * 100) : 0,
  })).sort((a, b) => b.count - a.count);

  const topLabel = total > 0 ? rows[0].label : null;

  return (
    <div className="pt-3 border-t border-gray-200">
      <div className="flex items-center justify-between mb-2.5">
        <h4 className="font-semibold text-gray-900 text-sm">How You Dial</h4>
        {topLabel ? (
          <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
            Most used · <span className="text-gray-700">{topLabel}</span>
          </span>
        ) : (
          <span className="text-[10px] text-gray-400">No outbound calls yet</span>
        )}
      </div>

      {/* Segmented usage bar */}
      <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-gray-100">
        {total > 0 &&
          rows
            .filter((r) => r.count > 0)
            .map((r) => (
              <div
                key={r.key}
                className={`${r.bar} h-full first:rounded-l-full last:rounded-r-full transition-all`}
                style={{ width: `${(r.count / total) * 100}%` }}
                title={`${r.label}: ${r.count} (${r.pct}%)`}
              />
            ))}
      </div>

      {/* Ranked rows */}
      <div className="mt-3 space-y-1.5">
        {rows.map((r, i) => {
          const Icon = r.icon;
          const isTop = i === 0 && total > 0 && r.count > 0;
          return (
            <div
              key={r.key}
              className={`flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 ${isTop ? "bg-gray-50" : ""}`}
            >
              <span
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md"
                style={{ backgroundColor: `${r.color}1a` }}
              >
                <Icon className="h-3.5 w-3.5" style={{ color: r.color }} />
              </span>
              <span className="text-xs font-medium text-gray-700">{r.label}</span>
              {isTop && (
                <span className={`rounded-full px-1.5 py-0.5 text-[8px] font-bold uppercase ${r.chip}`}>
                  Top
                </span>
              )}
              <span className="ml-auto flex items-baseline gap-1.5">
                <span className="text-sm font-bold text-gray-900 tabular-nums">
                  {r.count.toLocaleString()}
                </span>
                <span className="text-[10px] text-gray-400 tabular-nums">{r.pct}%</span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
